import { useEffect } from 'react';
import ChatHomeLayout from './layout/ChatHomeLayout';
import ChatProvider, { useChatContext } from './context/ChatProvider';
import {
  ChatModalType,
  ChatStatus,
  type ChatMessage,
  type EndChatExportOptions,
} from './lib/types';
import ConsentConfirmation from './components/modals/ConsentConfirmation';
import EndChatConfirmation from './components/modals/EndChatConfirmation';
import PatientConfirmation from './components/modals/PatientConfirmation';
import ChatOnboadingScreen from './screens/ChatOnboadingScreen';
import ChatMainScreen from './screens/ChatMainScreen';
import { sendEmail } from '../../../api';
import { exportChatTranscript } from './lib/exportChatTranscript';
import {
  SendEmailTemplate,
  type EndozoneChatHistoryItem,
  type EndozoneChatHistoryResource,
} from '../../../lib/payloads/sendEmail';
import { formatChatConversationStartedAt } from '../../helpers/dateHelper';
import { useToast } from '../../../context';

const EMAIL_CHAT_CREATED_AT_FORMAT = 'DD/MM/YYYY hh:mm a';
const CHAT_CREATED_AT_FALLBACK_ISO = () => new Date().toISOString();

const formatEmailChatCreatedAt = (conversationStartedAt: string | null) =>
  formatChatConversationStartedAt(
    conversationStartedAt ?? CHAT_CREATED_AT_FALLBACK_ISO(),
    EMAIL_CHAT_CREATED_AT_FORMAT,
  );

const getMessageResources = (
  message: ChatMessage,
): EndozoneChatHistoryResource[] => {
  if (!Array.isArray(message.data?.endoPatientResources)) {
    return [];
  }

  return message.data.endoPatientResources.filter(
    (item): item is EndozoneChatHistoryResource =>
      Boolean(item) &&
      typeof item === 'object' &&
      typeof item.title === 'string' &&
      typeof item.description === 'string',
  );
};

const mapMessagesToChatHistory = (
  messages: ChatMessage[],
): EndozoneChatHistoryItem[] =>
  messages.map((message) => {
    const resources = getMessageResources(message);
    return {
      timeStamp: message.createdAt,
      content: `${
        message.role === 'assistant'
          ? String(message.data?.authorName || 'EndoZoneAI')
          : 'You'
      }: ${message.text}`,
      resources: resources.length > 0 ? resources : undefined,
    };
  });

const ChatHomeContent = () => {
  const {
    chatStatus,
    modalType,
    conversationStartedAt,
    messages,
    patientConfirmationDetails,
    setModalType,
    setPatientConfirmationDetails,
    moveToOnboard,
    moveToChat,
  } = useChatContext();
  const { setToast } = useToast();

  useEffect(() => {
    const handleRequestEndChat = () => {
      if (chatStatus === ChatStatus.CHAT) {
        setModalType(ChatModalType.END);
      }
    };

    window.addEventListener('chat-home:request-end-chat', handleRequestEndChat);

    return () => {
      window.removeEventListener(
        'chat-home:request-end-chat',
        handleRequestEndChat,
      );
    };
  }, [chatStatus, setModalType]);

  const onClose = () => {
    setModalType(ChatModalType.NONE);
    if (modalType === ChatModalType.PATIENT) {
      setPatientConfirmationDetails(null);
    }
  };

  const handleEndChatConfirm = async (options: EndChatExportOptions) => {
    try {
      if (options.email) {
        await sendEmail({
          template: SendEmailTemplate.ENDOZONE_CHAT_HISTORY,
          templateVariables: {
            chatCreatedAt: formatEmailChatCreatedAt(conversationStartedAt),
            chatHistory: mapMessagesToChatHistory(messages),
          },
        });
      }

      await exportChatTranscript({
        messages,
        conversationStartedAt,
        shouldDownload: options.download,
        shouldPrint: options.print,
      });

      moveToOnboard();
    } catch (error) {
      setToast('Failed to end chat ❌');
    }
  };

  return (
    <ChatHomeLayout>
      {chatStatus === ChatStatus.ONBOARDING && <ChatOnboadingScreen />}
      {chatStatus === ChatStatus.CHAT && <ChatMainScreen />}
      {modalType === ChatModalType.PATIENT && (
        <PatientConfirmation
          show
          details={patientConfirmationDetails}
          onClose={onClose}
          onCancel={onClose}
          onConfirm={() => setModalType(ChatModalType.CONSENT)}
        />
      )}
      {modalType === ChatModalType.CONSENT && (
        <ConsentConfirmation
          show
          onClose={onClose}
          onCancel={onClose}
          onConfirm={() => {
            setModalType(ChatModalType.NONE);
            moveToChat();
          }}
        />
      )}
      {modalType === ChatModalType.END && (
        <EndChatConfirmation
          show
          onClose={onClose}
          onCancel={onClose}
          onConfirm={handleEndChatConfirm}
        />
      )}
    </ChatHomeLayout>
  );
};

const ChatHome = () => {
  return (
    <ChatProvider>
      <ChatHomeContent />
    </ChatProvider>
  );
};

export default ChatHome;
